// ReconnectingWebSocket — a thin wrapper around the native WebSocket API that
// automatically reconnects on unexpected closure with exponential backoff.
//
// Reconnect schedule: 1s → 2s → 4s → 8s → 15s (cap), each with ±20% jitter.
// The backoff resets to 0 after a successful open.
//
// An explicit call to close() permanently stops reconnection.  This is the
// mechanism callers use to signal intentional disconnect (e.g. user navigates
// away, transport switch).
//
// Network-level drops (e.g. WiFi toggled off) do not immediately fire onclose
// in Chrome — the TCP socket goes into a zombie state.  This class listens for
// the window 'offline' event to detect such drops and closes the socket itself,
// triggering the normal reconnect path.  The 'online' event cancels any pending
// backoff timer and reconnects immediately.
//
// Callers register event handlers as properties (ws.onmessage = fn), exactly
// as they would on a raw WebSocket.  Handlers are re-applied to each new
// underlying socket automatically on reconnect.
//
// `url` may be a string, or a function called before every attempt as
// url({ reconnect }) and returning a string or a Promise of one. The function
// form exists for URLs that go stale: the Midulate WS URL carries a session
// token that the device re-mints on every boot, so after a power cycle a fixed
// URL can never connect again. A rejected promise (or a throw) counts as a
// failed attempt and backs off like any other (MDL-384).

const BACKOFF_STEPS_MS    = [1000, 2000, 4000, 8000, 15000];
const JITTER_FACTOR       = 0.2; // ±20%
// Application-level heartbeat: send a data frame every 15 s to keep the
// connection alive across NAT and browser idle-connection timeouts.
const HEARTBEAT_INTERVAL_MS = 15000;

function withJitter(ms) {
  const jitter = ms * JITTER_FACTOR;
  return ms + (Math.random() * 2 - 1) * jitter;
}

export default class ReconnectingWebSocket {
  constructor(url, { WebSocketClass = WebSocket, _setTimeout = setTimeout, _clearTimeout = clearTimeout, _setInterval = setInterval, _clearInterval = clearInterval, _window = window } = {}) {
    this.url             = url;
    this._WS             = WebSocketClass;
    this._setTimeout     = (fn, ms) => _setTimeout(fn, ms);
    this._clearTimeout   = (id) => _clearTimeout(id);
    this._setInterval    = (fn, ms) => _setInterval(fn, ms);
    this._clearInterval  = (id) => _clearInterval(id);
    this._window         = _window;

    this.onmessage = null;
    this.onerror   = null;
    // onConnectionStateChanged(state) — 'connected' | 'reconnecting'
    this.onConnectionStateChanged = null;

    this._socket          = null;
    this._attempts        = 0;     // connect attempts since open(); 0 = the first
    this._attemptSeq      = 0;     // identifies the latest attempt, so a slow URL
                                   // resolution that has been overtaken is dropped
    this._resolving       = false; // true while a url() promise is outstanding
    this._backoffIndex    = 0;
    this._retryTimer      = null;
    this._heartbeatTimer  = null;
    this._closed          = false; // true after explicit close()

    // Chrome does not close WebSocket connections when the network interface
    // goes down — we must do it ourselves so the reconnect loop kicks in.
    this._offlineHandler = () => {
      if (this._socket && !this._closed) {
        // Suppress the onclose handler — we're scheduling the reconnect ourselves
        // immediately rather than waiting for the WS close handshake, which can't
        // complete when the network is already gone.
        this._socket.onclose = null;
        this._socket.close();
        this._socket = null;
        this._scheduleReconnect();
      }
    };

    // When the network returns, skip the remaining backoff and reconnect now.
    this._onlineHandler = () => {
      if (!this._closed && this._retryTimer !== null) {
        this._clearTimeout(this._retryTimer);
        this._retryTimer = null;
        this._connect();
      }
    };

    // Safari (desktop and iOS) kills WebSocket connections when a page is
    // backgrounded or the screen locks, and may not fire onclose until the
    // page is foregrounded again.  When the page becomes visible, if no socket
    // is active (connection was lost while hidden), skip any remaining backoff
    // and reconnect immediately.
    this._visibilityHandler = () => {
      if (this._window.document.visibilityState === 'visible' && !this._closed
          && this._socket === null && !this._resolving) {
        if (this._retryTimer !== null) {
          this._clearTimeout(this._retryTimer);
          this._retryTimer = null;
        }
        this._connect();
      }
    };
  }

  // Open the initial connection.  Called once by the owner; reconnects are
  // handled internally.
  open() {
    this._closed = false;
    this._attempts = 0;
    this._window.addEventListener('offline', this._offlineHandler);
    this._window.addEventListener('online',  this._onlineHandler);
    this._window.document.addEventListener('visibilitychange', this._visibilityHandler);
    this._connect();
  }

  // Permanently stop reconnection and close any live socket.
  // After this call no further reconnect will be attempted.
  close() {
    this._closed = true;
    this._window.removeEventListener('offline', this._offlineHandler);
    this._window.removeEventListener('online',  this._onlineHandler);
    this._window.document.removeEventListener('visibilitychange', this._visibilityHandler);
    if (this._retryTimer !== null) {
      this._clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    if (this._heartbeatTimer !== null) {
      this._clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._socket) {
      this._socket.onclose = null; // prevent _handleClose from firing
      this._socket.close();
      this._socket = null;
    }
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  _connect() {
    const reconnect = this._attempts > 0;
    this._attempts++;
    const seq = ++this._attemptSeq;

    let url;
    try {
      url = typeof this.url === 'function' ? this.url({ reconnect }) : this.url;
    } catch (_) {
      if (!this._closed) this._scheduleReconnect();
      return;
    }

    // A plain string opens synchronously, exactly as before the function form
    // existed — so the initial connect is unchanged for every caller.
    if (!url || typeof url.then !== 'function') {
      this._openSocket(url);
      return;
    }

    this._resolving = true;
    url.then(
      (resolved) => {
        this._resolving = false;
        if (this._closed || seq !== this._attemptSeq) return;
        this._openSocket(resolved);
      },
      () => {
        this._resolving = false;
        if (this._closed || seq !== this._attemptSeq) return;
        this._scheduleReconnect();
      },
    );
  }

  _openSocket(url) {
    const ws = new this._WS(url);
    this._socket = ws;

    ws.onmessage = (evt) => {
      if (ws !== this._socket) return;
      if (this.onmessage) this.onmessage(evt);
    };

    ws.onerror = (evt) => {
      if (ws !== this._socket) return;
      if (this.onerror) this.onerror(evt);
      // onerror is always followed by onclose — reconnect logic lives there.
    };

    ws.onopen = () => {
      if (ws !== this._socket) return;
      this._backoffIndex = 0;
      // Periodic heartbeat — keeps connection alive; device ignores unknown text frames.
      this._heartbeatTimer = this._setInterval(() => {
        if (this._socket === ws && ws.readyState === 1 /* OPEN */) {
          try { ws.send('{}'); } catch (_) {}
        }
      }, HEARTBEAT_INTERVAL_MS);
      if (this.onConnectionStateChanged) this.onConnectionStateChanged('connected');
    };

    ws.onclose = (evt) => {
      if (ws !== this._socket) return;
      if (this._heartbeatTimer !== null) {
        this._clearInterval(this._heartbeatTimer);
        this._heartbeatTimer = null;
      }
      this._socket = null;
      if (this._closed) return;
      this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    const delayMs = withJitter(BACKOFF_STEPS_MS[Math.min(this._backoffIndex, BACKOFF_STEPS_MS.length - 1)]);
    this._backoffIndex = Math.min(this._backoffIndex + 1, BACKOFF_STEPS_MS.length - 1);

    if (this.onConnectionStateChanged) this.onConnectionStateChanged('reconnecting');

    this._retryTimer = this._setTimeout(() => {
      this._retryTimer = null;
      if (!this._closed) this._connect();
    }, delayMs);
  }
}
